import { useNavigate } from "@tanstack/react-router";
import { ShoppingBag, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { formatPriceCents } from "@/features/orders/pricing";
import { cn } from "@/lib/utils";
import { cartCount, useCartStore } from "@/stores/useCartStore";
import { groupCartLinesByProject } from "../grouping";
import { useCartQuote } from "../useCartQuote";
import { CartLineRow, CartProjectHeader } from "./CartLineRow";

// The nav Cart affordance (ADR-0054 §2): a quiet icon + a live count badge that opens a
// slide-out Sheet mini-cart — the two-level project tree, per-line prices, a subtotal,
// and a "Przejdź do kasy" CTA that lands on the full cart (the checkout page). A quick
// peek + remove; quantities are edited on the checkout page (the mini-cart stays calm,
// per DESIGN.md "quiet workflow"). Placed in both the desktop bar and the mobile strip.
export function CartNav({ triggerClassName }: { triggerClassName: string }) {
  const { t } = useTranslation("orders");
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = useCartStore((s) => s.items);
  const removeProduct = useCartStore((s) => s.removeProduct);
  const count = cartCount(items);

  // Only quote while the Sheet is open (no request for a cart nobody's looking at). The
  // hook also prunes lines the server can no longer quote, so peeking at the cart is
  // enough to heal it — and the badge stops counting products that are gone.
  const { quote, isError, expired } = useCartQuote({ enabled: open });
  const groups = quote ? groupCartLinesByProject(quote.lines) : [];
  // "Przejdź do kasy" lands on the checkout page — the full cart. Project-agnostic: the
  // URL carries no project id, since the cart can hold several projects (ADR-0056).
  function goToCheckout() {
    setOpen(false);
    void navigate({ to: "/checkout" });
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t("cart.open")}
          data-testid="cart-nav-trigger"
          className={cn("relative", triggerClassName)}
        >
          <ShoppingBag aria-hidden="true" />
          {count > 0 ? (
            <span
              // The live count badge — coral pip on the bag corner. tabular-nums keeps a
              // two-digit count from shifting the glyph.
              className="absolute -top-0.5 -right-0.5 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[0.625rem] font-semibold text-primary-foreground tabular-nums"
              data-testid="cart-nav-count"
            >
              {count}
            </span>
          ) : null}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        data-testid="cart-nav-sheet"
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="flex w-full flex-col gap-0 border-chrome-line/25 bg-chrome pt-[max(1.5rem,env(safe-area-inset-top))] sm:max-w-md [&>button:first-of-type]:hidden"
      >
        <SheetHeader className="flex-row items-center justify-between space-y-0 border-b border-border/60 pb-4">
          <SheetTitle className="font-display tracking-tight">{t("cart.title")}</SheetTitle>
          <SheetClose className="inline-flex size-9 items-center justify-center rounded-full text-foreground/70 transition-colors hover:bg-foreground/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50">
            <X aria-hidden="true" className="size-5" />
            <span className="sr-only">{t("cart.close")}</span>
          </SheetClose>
        </SheetHeader>

        {items.length === 0 ? (
          <div
            className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center"
            data-testid="cart-nav-empty"
          >
            <ShoppingBag aria-hidden className="size-8 text-muted-foreground/60" />
            <p className="font-medium">{t("cart.empty")}</p>
            <p className="text-balance text-sm text-muted-foreground">{t("cart.emptyHint")}</p>
          </div>
        ) : (
          <>
            <div className="flex flex-1 flex-col gap-5 overflow-y-auto py-5">
              {/* Expired lines were just dropped (`useCartQuote`) — say so instead of
                  silently shrinking the cart under the buyer. */}
              {expired ? (
                <p
                  role="status"
                  className="rounded-lg bg-muted/40 px-3 py-2 text-sm text-muted-foreground"
                  data-testid="cart-nav-expired"
                >
                  {t("cart.expiredNotice")}
                </p>
              ) : null}
              {/* The quote couldn't be fetched at all (network / 5xx, retries spent). The
                  cart lines are unknown, so say that rather than render an empty tree. */}
              {isError && !quote ? (
                <p className="px-1 text-sm text-muted-foreground" data-testid="cart-nav-load-error">
                  {t("cart.loadError")}
                </p>
              ) : null}
              {groups.map((group) => (
                <div key={group.projectId ?? "catalog"} className="flex flex-col gap-3">
                  <CartProjectHeader group={group} t={t} />
                  <div className="flex flex-col gap-3">
                    {group.lines.map((line) => (
                      <CartLineRow
                        key={line.productId}
                        line={line}
                        currency={quote?.currency ?? "PLN"}
                        onRemove={() => removeProduct(line.productId)}
                        t={t}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-4 border-t border-border/60 pt-4">
              <div
                className="flex items-center justify-between text-sm"
                data-testid="cart-nav-subtotal"
              >
                <span className="text-muted-foreground">{t("cart.subtotal")}</span>
                <span className="font-semibold tabular-nums">
                  {quote ? formatPriceCents(quote.subtotalCents, quote.currency) : "—"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">{t("cart.shippingNote")}</p>
              <Button
                type="button"
                size="lg"
                className="min-h-12 w-full rounded-full text-base font-semibold"
                onClick={goToCheckout}
                data-testid="cart-nav-checkout"
              >
                {t("cart.goToCheckout")}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
